import { timer } from './helpers'
import { loadScriptBase } from './loadScriptBase'

type LoadingScript = HTMLScriptElement & { scriptLoadingState?: 'loading' | 'loaded' | 'error' }

export const loadScript = ({
    src,
    async = true,
    timeoutMs,
    useExisting = false,
    onBeforeFirstLoad,
}: {
    src: string
    async?: boolean
    timeoutMs?: number
    /**
     * It the script with same src already exists, it will be used instead of creating a new one
     */
    useExisting?: boolean
    onBeforeFirstLoad?: (script: HTMLScriptElement) => void
}): Promise<HTMLScriptElement> => {
    if (useExisting) {
        const existingScript = Array.from(document.scripts).find(
            (scriptElement) => scriptElement.src === src,
        ) as LoadingScript
        if (existingScript) {
            if (existingScript.scriptLoadingState === 'loaded') {
                return Promise.resolve(existingScript)
            }

            if (existingScript.scriptLoadingState === 'error') {
                return Promise.reject(new Error(`Script ${src} has failed state`))
            }

            return new Promise((resolve, reject) => {
                existingScript.addEventListener('load', () => resolve(existingScript))
                existingScript.addEventListener('error', () => reject(new Error(`Script ${src} has failed state`)))
            })
        }
    }

    const abortController = new AbortController()
    if (timeoutMs) {
        timer(timeoutMs).then(() => {
            abortController.abort()
        })
    }

    let loadingScript: LoadingScript
    return loadScriptBase({
        document,
        src,
        abortSignal: abortController.signal,
        onBeforeLoad: (script: LoadingScript) => {
            loadingScript = script as LoadingScript
            script.async = async
            script.scriptLoadingState = 'loading'

            onBeforeFirstLoad?.(script)
        },
    }).catch((error) => {
        loadingScript.scriptLoadingState = 'error'

        throw error
    })
}
